import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../api/client';
import './TemplateChangePreview.css';
import { translateError } from '../utils/errorMessages';

// =============================================================================
// TYPES
// =============================================================================

interface TemplateChanges {
  name?: string;
  description?: string;
  location?: string;
  firstOccurrence?: string;
  startTime?: string;
  endTime?: string;
  recurringPattern?: string;
  members?: (string | number)[];
  groups?: (string | number)[];
  coveredByMembershipIds?: (string | number)[];
  defaultAttendance?: boolean | null;
  isActive?: boolean;
  responsible?: string;
  activityId?: string | number | null;
  courseId?: string | number | null;
}

interface AffectedEvent {
  _id: string;
  name: string;
  startTime: string;
  hasManualChanges: boolean;
}

interface PreviewResponse {
  changedFields: string[];
  scheduleChanged: boolean;
  affectedEvents: AffectedEvent[];
  eventsWithManualChanges: number;
  totalAffectedEvents: number;
}

interface TemplateChangePreviewProps {
  templateId: string;
  /** Unified list of changed field names, computed by the edit page (single
   *  source of truth shared with `hasChanges`). */
  changedFields: string[];
  currentValues: TemplateChanges;
  onPreviewLoaded?: (preview: PreviewResponse | null) => void;
}

// Fields that indicate schedule-related changes
const SCHEDULE_FIELDS = ['firstOccurrence', 'startTime', 'endTime', 'recurringPattern'];

// =============================================================================
// COMPONENT
// =============================================================================

const TemplateChangePreview: React.FC<TemplateChangePreviewProps> = ({
  templateId,
  changedFields,
  currentValues,
  onPreviewLoaded,
}) => {
  const { t } = useTranslation();
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Change detection is owned by the edit page (unified with `hasChanges`); this
  // component only renders the provided list and fetches the affected-event count.
  const hasChanges = changedFields.length > 0;
  const scheduleChanged = changedFields.some((field) => SCHEDULE_FIELDS.includes(field));

  // Fetch preview from backend when there are changes
  const fetchPreview = useCallback(async () => {
    if (!hasChanges || !templateId) {
      setPreview(null);
      onPreviewLoaded?.(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.post<PreviewResponse>(
        `/api/event-templates/${templateId}/preview-changes`,
        { changes: currentValues }
      );
      setPreview(response.data);
      onPreviewLoaded?.(response.data);
    } catch (err: any) {
      setError(translateError(err, t));
      setPreview(null);
      onPreviewLoaded?.(null);
    } finally {
      setLoading(false);
    }
  }, [templateId, currentValues, hasChanges, onPreviewLoaded]);

  // Debounce the preview fetch
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchPreview();
    }, 500);

    return () => clearTimeout(timer);
  }, [fetchPreview]);

  // Don't render anything if no changes
  if (!hasChanges) {
    return null;
  }

  const getFieldLabel = (field: string): string => {
    const labels: Record<string, string> = {
      name: t('common.name'),
      description: t('common.description'),
      location: t('events.location'),
      firstOccurrence: t('templates.firstOccurrence'),
      startTime: t('events.startTime'),
      endTime: t('events.endTime'),
      recurringPattern: t('templates.recurrencePattern'),
      members: t('events.assignMembers'),
      groups: t('events.assignGroups'),
      coveredByMembershipIds: t('events.coveredBy'),
      defaultAttendance: t('events.defaultAttendance'),
      isActive: t('templates.isActive'),
      responsible: t('events.responsible'),
      activityId: t('templates.activity'),
      courseId: t('templates.course'),
    };
    return labels[field] || field;
  };

  return (
    <div className={`template-change-preview ${scheduleChanged ? 'schedule-warning' : ''}`}>
      <div className="template-change-preview__header">
        <span className="template-change-preview__icon">!</span>
        <span className="template-change-preview__title">
          {t('templateChanges.changesDetected', 'Changes detected')}
        </span>
      </div>

      <div className="template-change-preview__content">
        {/* Changed fields list */}
        <div className="template-change-preview__fields">
          <span className="template-change-preview__label">
            {t('templateChanges.changedFields', 'Changed fields')}:
          </span>
          <ul className="template-change-preview__field-list">
            {changedFields.map((field) => (
              <li
                key={field}
                className={`template-change-preview__field ${
                  SCHEDULE_FIELDS.includes(field) ? 'schedule-field' : ''
                }`}
              >
                {getFieldLabel(field)}
              </li>
            ))}
          </ul>
        </div>

        {/* Loading state */}
        {loading && (
          <div className="template-change-preview__loading">
            {t('common.loading')}
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="template-change-preview__error">
            {error}
          </div>
        )}

        {/* Preview results */}
        {preview && !loading && (
          <div className="template-change-preview__results">
            <div className="template-change-preview__stat">
              <span className="template-change-preview__stat-value">
                {preview.totalAffectedEvents}
              </span>
              <span className="template-change-preview__stat-label">
                {t('templateChanges.eventsAffected', 'events will be affected')}
              </span>
            </div>

            {preview.eventsWithManualChanges > 0 && (
              <div className="template-change-preview__warning">
                <span className="template-change-preview__warning-icon">*</span>
                <span>
                  {t(
                    'templateChanges.eventsWithCustomizations',
                    '{{count}} event(s) have manual customizations that may be affected',
                    { count: preview.eventsWithManualChanges }
                  )}
                </span>
              </div>
            )}

            {scheduleChanged && (
              <div className="template-change-preview__danger">
                <strong>{t('templateChanges.scheduleWarningTitle', 'Schedule Change Warning')}</strong>
                <p>
                  {t(
                    'templateChanges.scheduleWarning',
                    'Schedule-related fields have changed. Existing events may be regenerated based on the new schedule.'
                  )}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TemplateChangePreview;
